// color ideas!
// https://www.heavy.ai/blog/12-color-palettes-for-telling-better-stories-with-your-data
// https://carbondesignsystem.com/data-visualization/color-palettes/

import countryCodeLookup from "country-code-lookup";
import * as openalexId from "@/openalexId";

const entityConfigs = {
    works: {
        icon: "mdi-file-document-outline",
        name: "works",
        entityType: "works",
        nameSingular: "work",
        displayName: "works",
        displayNameSingular: "work",
        exportMode: "async",
        category: "research-output",
        descr: "Supply chain papers, books, datasets, and preprints",
        eg: "Supply chain resilience",
        placeholder: "Search supply chain research",
        filterName: "work",
        filterKey: "ids.openalex",
        hintVerb: "by",
        color: "blue",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        highlightFilters: [
            {key: "open_access.is_oa", value: true, displayName: "Open Access works"},
            {key: "institutions.is_global_south", value: true, displayName: "from the Global South"},
            {key: "type", value: "dataset", displayName: "datasets"},
        ],
        rowsToShowOnEntityPage: [
            "publication_year",
            "type",
            "abstract",
            "primary_location.source.id",
            "authorships.author.id",
            "authorships.institutions.lineage",
            "language",
            null,
            "primary_topic.id",
            "primary_topic.subfield.id",
            "primary_topic.field.id",
            "primary_topic.domain.id",
            "sustainable_development_goals.id",
            null,
            "open_access.oa_status",
            "apc_paid.value_usd",
            null,
            "funders.id",
            "awards.id",
        ],
        metricsToShowOnEntityPage: [
            // "cited_by_count" intentionally omitted — the works facet at facetConfigs.js
            // has no extractFn, and "cited_by" below renders the same number with the
            // filter:cites:W… click-through users actually want (zd#8767).
            "fwci",
            "citation_normalized_percentile.value",
            "cites",
            "cited_by",
            "related_to",
        ],
        groupByDefaults: [
            "publication_year",
            "open_access.is_oa",
            "primary_topic.id",
            "authorships.institutions.lineage",
            "type",
        ],
        // Default table-view columns (ordered). The mandatory `display_name`
        // identity column leads. Other entity types have no `defaultColumns`
        // yet; useColumnsState falls back to ["display_name"] (which renders as
        // a link to the row). Phase 6's catalog sweep adds per-property column
        // blocks and richer per-entity defaults.
        defaultColumns: [
            "display_name",
            "authorships.author.id",
            "publication_year",
            "cited_by_count",
            "open_access.is_oa",
        ],
    },
    awards: {
        icon: "mdi-cash-multiple",
        name: "awards",
        entityType: "awards",
        nameSingular: "award",
        displayName: "awards",
        displayNameSingular: "award",
        exportMode: "async",
        category: "funding",
        descr: "Grants funding scholarly research",
        eg: "Fonds de recherche du Québec",
        placeholder: "Search grant awards",
        filterName: "award",
        filterKey: "awards.id",
        hintVerb: "funded by",
        color: "brown",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: false,
        rowsToShowOnEntityPage: [
            "description",
            null,
            "funder.id",
            "amount",
            "funding_type",
            "funder_scheme",
            null,
            "start_date",
            "end_date",
            null,
            "investigators",
            "investigators.affiliation",
            null,
            "primary_topic.id",
            "primary_topic.subfield.id",
            "primary_topic.field.id",
            "primary_topic.domain.id",
            null,
            "funded_outputs_count",
            "funder_award_id",
        ],
        groupByDefaults: [
            "start_year",
            "funder.id",
            "primary_topic.id",
            "funding_type",
        ],
    },
    authors: {
        // icon: "mdi-account-school-outline",
        icon: "mdi-account-outline",
        name: "authors",
        entityType: "authors",
        nameSingular: "author",
        displayName: "authors",
        displayNameSingular: "author",
        exportMode: "async",
        category: "people-orgs",
        descr: "Creators of scholarly works",
        eg: "Albert Einstein",
        placeholder: "Search scholarly authors",
        filterName: "author",
        filterKey: "authorships.author.id",
        hintVerb: "at",
        color: "green",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        highlightFilters: [
            {key: "has_orcid", value: true, displayName: "with ORCIDs"},
            {key: "last_known_institutions.is_global_south", value: true, displayName: "from the Global South"},
        ],
        rowsToShowOnEntityPage: [
            "display_name_alternatives",
            "last_known_institutions.id",
            "affiliations.institution.id",
            "ids.orcid",
        ],
        metricsToShowOnEntityPage: [
            "works_count",
            "cited_by_count",
            "summary_stats.h_index",
            "summary_stats.i10_index",
        ],
        groupByDefaults: [
            "last_known_institutions.id",
            "last_known_institutions.country_code",
            "has_orcid",
        ],
        // Default table-view columns + the list-view export seed (job #304).
        defaultColumns: [
            "display_name",
            "last_known_institutions.id",
            "works_count",
            "cited_by_count",
            "summary_stats.h_index",
        ],
    },
    sources: {
        // icon: "mdi-book-outline",
        icon: "mdi-book-open-outline",
        name: "sources",
        entityType: "sources",
        nameSingular: "source",
        displayName: "sources",
        displayNameSingular: "source",
        exportMode: "async",
        category: "publishing",
        descr: "Journals, conferences, and repositories",
        eg: "The New England Journal of Medicine",
        placeholder: "Search academic journals & repositories",
        filterName: "primary_location.source",
        filterKey: "primary_location.source.id",
        hintVerb: "published by",
        color: "orange",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        highlightFilters: [
            {key: "is_oa", value: true, displayName: "that are Open Access"},
        ],
        rowsToShowOnEntityPage: [
            "homepage_url",
            "issn",
            "type",
            "publisher",
            "alternate_titles",
            null,
            "is_oa",
            "is_in_doaj",
            "apc_usd",
        ],
        metricsToShowOnEntityPage: [
            "works_count",
            "cited_by_count",
            "summary_stats.h_index",
            "summary_stats.i10_index",
            "summary_stats.2yr_mean_citedness",
        ],
        groupByDefaults: [
            "type",
            "topics.id",
            "is_oa",
            "is_in_doaj",
        ],
        defaultColumns: [
            "display_name",
            "type",
            "publisher",
            "works_count",
            "cited_by_count",
        ],
    },
    publishers: {
        // icon: "mdi-book-outline",
        icon: "mdi-domain",
        name: "publishers",
        entityType: "publishers",
        nameSingular: "publisher",
        displayName: "publishers",
        displayNameSingular: "publisher",
        exportMode: "async",
        category: "publishing",
        descr: "Organizations that publish scholarly journals and books",
        eg: "Elsevier",
        placeholder: "Search academic publishers",
        filterName: "primary_location.source.publisher_lineage",
        filterKey: "primary_location.source.publisher_lineage",
        color: "pink",
        hasAutocomplete: true,
        isNative: true,
        rowsToShowOnEntityPage: [
            "homepage_url",
            "alternate_titles",
            "parent_publisher",
            "country_codes",
        ],
        defaultColumns: [
            "display_name",
            "country_codes",
            "works_count",
            "cited_by_count",
        ],
    },
    funders: {
        icon: "mdi-cash-multiple",
        name: "funders",
        entityType: "funders",
        nameSingular: "funder",
        displayName: "funders",
        displayNameSingular: "funder",
        exportMode: "async",
        category: "funding",
        descr: "Organization that funds research",
        eg: "US National Science Foundation",
        placeholder: "Search research funders",
        filterName: "funders",
        filterKey: "funders.id",
        hintVerb: "funded by",
        color: "brown",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: false,
        rowsToShowOnEntityPage: [
            "homepage_url",
            "alternate_titles",
            "description",
            null,
            "country_code",
            "ids.ror",
            "ids.wikidata",
            "ids.crossref",
            "ids.doi",
        ],
        metricsToShowOnEntityPage: [
            "works_count",
            "cited_by_count",
            "summary_stats.h_index",
            "summary_stats.i10_index",
            "summary_stats.2yr_mean_citedness",
            "awards_count",
        ],
        groupByDefaults: [
            "country_code",
            "is_global_south",
        ],
        defaultColumns: [
            "display_name",
            "country_code",
            "works_count",
            "cited_by_count",
        ],
    },
    institutions: {
        icon: "mdi-town-hall",
        name: "institutions",
        entityType: "institutions",
        nameSingular: "institution",
        displayName: "institutions",
        displayNameSingular: "institution",
        exportMode: "async",
        category: "people-orgs",
        descr: "Universities and research centers",
        eg: "Harvard University",
        placeholder: "Search academic institutions",
        filterName: "institutions",
        filterKey: "authorships.institutions.lineage",
        hintVerb: "in",
        color: "purple",
        hasAutocomplete: true,
        isNative: true,
        hasSerp: true,
        rowsToShowOnEntityPage: [
            "homepage_url",
            "display_name_alternatives",
            "parent_institutions",
            "child_institutions",
            "related_institutions",
            "ids.ror",
        ],
        groupByDefaults: [
            "country_code",
            "type",
        ],
        defaultColumns: [
            "display_name",
            "country_code",
            "type",
            "works_count",
            "cited_by_count",
        ],
    },
    concepts: {
        icon: "mdi-tag-outline",
        name: "concepts",
        entityType: "concepts",
        nameSingular: "concept",
        displayName: "concepts",
        displayNameSingular: "concept",
        category: "topics",
        descr: "Topics and fields of study",
        eg: "History",
        placeholder: "Search topics",
        filterName: "concepts",
        filterKey: "concepts.id",
        hasAutocomplete: true,
        isNative: true,
        rowsToShowOnEntityPage: [],
        defaultColumns: [
            "display_name",
            "level",
            "works_count",
            "cited_by_count",
        ],
    },
    keywords: {
        icon: "mdi-tag-outline",
        name: "keywords",
        entityType: "keywords",
        nameSingular: "keyword",
        displayName: "keywords",
        displayNameSingular: "keyword",
        exportMode: "async",
        category: "topics",
        descr: "Author-assigned and extracted terms describing research content",
        eg: "Cardiac imaging",
        placeholder: "Search keywords",
        filterName: "keywords",
        filterKey: "keywords.id",
        hintVerb: ":",
        hasAutocomplete: true,
        isNative: false,
        hasSerp: true,
        rowsToShowOnEntityPage: [
        ],
    },
    topics: {
        icon: "mdi-tag-outline",
        name: "topics",
        entityType: "topics",
        nameSingular: "topic",
        displayName: "topics",
        displayNameSingular: "topic",
        exportMode: "async",
        category: "topics",
        descr: "Algorithmically clustered research areas (~4,500 entries)",
        eg: "RNA sequencing",
        placeholder: "Search topics",
        // hasSerp: true,
        hasSerp: true,
        filterName: "topics",
        filterKey: "primary_topic.id",
        hintVerb: ":",
        hasAutocomplete: true,
        isNative: true,
        rowsToShowOnEntityPage: [
            "description",
            null,
            "siblings",
            "subfield.id",
            "field.id",
            "domain.id",
        ],
        groupByDefaults: [
            "subfield.id",
            "field.id",
            "domain.id",
        ],
        defaultColumns: [
            "display_name",
            "subfield.id",
            "works_count",
            "cited_by_count",
        ],
    },
    subfields: {
        icon: "mdi-tag-outline",
        name: "subfields",
        entityType: "subfields",
        nameSingular: "subfield",
        displayName: "subfields",
        displayNameSingular: "subfield",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "field.display_name", label: "Field" },
            { key: "domain.display_name", label: "Domain" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "Mid-level academic disciplines (~250 entries) grouping related topics",
        eg: "Molecular biology",
        placeholder: "Search subfields",
        filterName: "subfields",
        filterKey: "primary_topic.subfield.id",
        hasAutocomplete: true,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "display_name_alternatives",
            null,
            "topics",
            "siblings",
            "field.id",
            "domain.id",
        ],
        groupByDefaults: [
            "field.id",
            "domain.id",
        ],
    },
    fields: {
        icon: "mdi-tag-outline",
        name: "fields",
        entityType: "fields",
        nameSingular: "field",
        displayName: "fields",
        displayNameSingular: "field",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "domain.display_name", label: "Domain" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "Broad academic disciplines (~25) like Biology, Chemistry, Physics",
        eg: "Computer science",
        placeholder: "Search fields",
        filterName: "fields",
        filterKey: "primary_topic.field.id",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "display_name_alternatives",
            null,
            "subfields",
            "siblings",
            "domain.id",
        ],
        groupByDefaults: [
            "domain.id",
        ],
    },
    domains: {
        icon: "mdi-tag-outline",
        name: "domains",
        entityType: "domains",
        nameSingular: "domain",
        displayName: "domains",
        displayNameSingular: "domain",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "Four top-level research areas: Life Sciences, Physical Sciences, Health Sciences, Social Sciences",
        eg: "Life sciences",
        placeholder: "Search domains",
        filterName: "domains",
        filterKey: "primary_topic.domain.id",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "display_name_alternatives",
            null,
            "fields",
            "siblings",
        ],
    },
    sdgs: {
        icon: "mdi-sprout-outline",
        name: "sdgs",
        entityType: "sdgs",
        nameSingular: "sdg",
        displayName: "Sustainable Development Goals",
        displayNameSingular: "Sustainable Development Goal",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "topics",
        descr: "United Nations Sustainable Development Goals linked to research",
        eg: "Clean water and sanitation",
        placeholder: "Search SDGs",
        filterName: "Sustainable Development Goals",
        filterKey: "sustainable_development_goals.id",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    countries: {
        icon: "mdi-earth",
        name: "countries",
        entityType: "countries",
        nameSingular: "country",
        displayName: "countries",
        displayNameSingular: "country",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "continent.display_name", label: "Continent" },
            { key: "is_global_south", label: "Global South" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "geo-language",
        descr: "Countries where research-affiliated institutions are located",
        eg: "Nigeria",
        placeholder: "Search countries",
        filterName: "countries",
        filterKey: "authorships.countries",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "display_name_alternatives",
            null,
            "continent",
            "is_global_south",
        ],
    },
    continents: {
        icon: "mdi-earth",
        name: "continents",
        entityType: "continents",
        nameSingular: "continent",
        displayName: "continents",
        displayNameSingular: "continent",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
        ],
        category: "geo-language",
        descr: "Continental regions for geographic analysis of research",
        eg: "Africa",
        placeholder: "Search continents",
        filterName: "continent",
        filterKey: "authorships.institutions.continent",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "countries",
        ],
    },
    languages: {
        icon: "mdi-translate",
        name: "languages",
        entityType: "languages",
        nameSingular: "language",
        displayName: "languages",
        displayNameSingular: "language",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "geo-language",
        descr: "Languages in which scholarly works are written",
        eg: "Swahili",
        placeholder: "Search languages",
        filterName: "languages",
        filterKey: "language",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [],
    },
    types: {
        icon: "mdi-shape-outline",
        name: "types",
        entityType: "types",
        nameSingular: "type",
        displayName: "types",
        displayNameSingular: "type",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "research-output",
        descr: "Formats of scholarly output: articles, books, datasets, preprints, etc.",
        eg: "article",
        placeholder: "Search work types",
        filterName: "type",
        filterKey: "type",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
            "crossref_types",
        ],
    },
    "source-types": {
        icon: "mdi-shape-outline",
        name: "source-types",
        entityType: "source-types",
        nameSingular: "source type",
        displayName: "source types",
        displayNameSingular: "source type",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "publishing",
        descr: "Categories of publication venues: journals, repositories, conferences",
        eg: "journal",
        placeholder: "Search source types",
        filterName: "source type",
        filterKey: "primary_location.source.type",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    "institution-types": {
        icon: "mdi-shape-outline",
        name: "institution-types",
        entityType: "institution-types",
        nameSingular: "institution type",
        displayName: "institution types",
        displayNameSingular: "institution type",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "people-orgs",
        descr: "Categories of organizations: universities, companies, hospitals, governments",
        eg: "company",
        placeholder: "Search institution types",
        filterName: "institution type",
        filterKey: "authorships.institutions.type",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    "licenses": {
        icon: "mdi-lock-open-outline",
        name: "licenses",
        entityType: "licenses",
        nameSingular: "license",
        displayName: "licenses",
        displayNameSingular: "license",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "open-access",
        descr: "Open Access licenses like CC-BY, CC-BY-SA, and others",
        eg: "CC-BY",
        placeholder: "Search licenses",
        filterName: "license",
        filterKey: "best_oa_location.license",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    "oa-statuses": {
        icon: "mdi-lock-open-outline",
        name: "oa-statuses",
        entityType: "oa-statuses",
        nameSingular: "oa status",
        displayName: "Open Access statuses",
        displayNameSingular: "Open Access status",
        exportMode: "client",
        exportColumns: [
            { key: "id", label: "ID" },
            { key: "display_name", label: "Name" },
            { key: "description", label: "Description" },
            { key: "works_count", label: "Works Count" },
            { key: "cited_by_count", label: "Cited By Count" },
        ],
        category: "open-access",
        descr: "Open Access classifications: gold, green, hybrid, bronze, closed",
        eg: "gold",
        placeholder: "Search OA statuses",
        filterName: "OA status",
        filterKey: "open_access.oa_status",
        hasAutocomplete: false,
        isNative: false,
        rowsToShowOnEntityPage: [
            "description",
        ],
    },
    "locations": {
        icon: "mdi-file-document-outline",
        name: "locations",
        entityType: "locations",
        nameSingular: "location",
        displayName: "locations",
        displayNameSingular: "work location",
        descr: "Work location",
        eg: "location",
        placeholder: "Search locations",
        filterName: "location",
        filterKey: "locations.id",
        hasAutocomplete: false,
        isNative: false,
        hasSerp: false,
        rowsToShowOnEntityPage: [
            "source_id",
            "landing_page_url",
            "pdf_url",
            null,
            "is_oa",
            "version",
            "license",
            null,
            "id",
            "provenance",
        ],
        metricsToShowOnEntityPage: [],
    },
}


const metricsPriorityOrder = [
    "works_count",
    "cited_by_count",
    "summary_stats.h_index",
    "summary_stats.i10_index",
    "summary_stats.2yr_mean_citedness",
    "awards_count",
    "fwci",
    "citation_normalized_percentile.value",
    "cites",
    "cited_by",
    "related_to",
]

const defaultMetricsToShowOnEntityPage = ["works_count", "cited_by_count"];
for (const c of Object.values(entityConfigs)) {
    if (c.metricsToShowOnEntityPage === undefined) {
        c.metricsToShowOnEntityPage = defaultMetricsToShowOnEntityPage;
    }
}

const entityConfigBySingular = Object.fromEntries(
    Object.values(entityConfigs).map(c => [c.nameSingular, c])
);


const entityCategories = [
    { id: 'research-output', name: 'Research output' },
    { id: 'people-orgs', name: 'People & organizations' },
    { id: 'publishing', name: 'Publishing' },
    { id: 'funding', name: 'Funding' },
    { id: 'topics', name: 'Topics & classification' },
    { id: 'geo-language', name: 'Geography & language' },
    { id: 'open-access', name: 'Open Access' },
];

const getEntitiesForBrowser = function () {
    return getEntityConfigs().filter(c =>
        c.hasSerp === true
    );
}

const getEntityConfigs = function () {
    return Object.values(entityConfigs);
}


const getEntityConfig = function (name) {
    if (!name) {
        console.trace();
        throw new Error("OpenAlex: getEntityConfig(): you must provide a @name argument.")
    }
    return entityConfigs[name] ?? entityConfigBySingular[name];
}


// Explicit mapping from OpenAlex ID prefix to native entity "name"
// e.g. W123 → "works", A123 → "authors", G123 → "awards"
const nativeIdPrefixToEntityType = {
    w: "works",
    a: "authors",
    i: "institutions",
    s: "sources",
    p: "publishers",
    f: "funders",
    c: "concepts",
    t: "topics",
    g: "awards", // G = awards
};

/**
 * @deprecated Use openalexId.getEntityType() instead
 */
const nativeEntityTypeFromId = function (id) {
    const parsed = openalexId.parseId(id);
    if (parsed && parsed.isNative) {
        return parsed.entityType;
    }
    return undefined;
}


/**
 * @deprecated Use openalexId.getEntityType() instead
 */
const externalEntityTypeFromId = function (id) {
    const parsed = openalexId.parseId(id);
    if (parsed && !parsed.isNative) {
        return parsed.entityType;
    }
    return undefined;
}

/**
 * @deprecated Use openalexId.parseId() instead
 */
const parseEntityId = function (id) {
    const parsed = openalexId.parseId(id);
    if (!parsed) {
        throw new Error(`OpenAlex: parseEntityId(): invalid id: ${id}`);
    }
    return {
        entityType: parsed.entityType,
        entityKey: parsed.shortId,
    };
}


/**
 * @deprecated Use openalexId.parseId() instead
 */
const urlPartsFromId = function (id) {
    const parsed = openalexId.parseId(id);
    if (!parsed) {
        return { entityType: undefined, entityId: undefined };
    }
    return {
        entityType: parsed.entityType,
        entityId: parsed.shortId,
    };
}

// maybe make something that parses an id, no matter what the format, and returns the entityType and entityId

const getLocationString = function (entity) {
    if (!entity || !entity?.country_code) return
    const country = countryCodeLookup.byIso(entity?.country_code)
        ?.country
        ?.replace("United States", "USA")
        ?.replace("United Kingdom", "UK")

    const locArr = [
        entity?.geo?.city,
        entity?.geo?.region,
        country,
    ].filter(x => x)
    return locArr.join(", ")
}


export {
    entityConfigs,
    entityCategories,
    metricsPriorityOrder,
    getEntityConfig,
    getEntityConfigs,
    getEntitiesForBrowser,
    getLocationString,
    nativeEntityTypeFromId,
    externalEntityTypeFromId,
    urlPartsFromId,
    parseEntityId,
}
